/**
 * The kind of site that sent a visitor, as a coarse bucket — for the cookieless counters
 * (functions/_middleware.ts landings, functions/api/stats.ts event views, the /go/ click's `s`) and
 * the browser's own note of it (src/lib/visit.ts). A bucket names a kind of site, never a page, a
 * person or an identifier: what the privacy policy promises (src/pages/Legal.tsx, "Counting
 * without identifying you").
 *
 * Imported by the edge AND the browser bundle: no affiliate ids here (never import go-gate.mjs).
 */

/** Every bucket a source can be. */
export const SOURCES = ['chatgpt', 'copilot-bing', 'google', 'gemini', 'perplexity', 'claude', 'ddg', 'alert', 'internal', 'none', 'other'];

/** utm_source values we know (ChatGPT appends utm_source=chatgpt.com; our price-alert emails say alert). */
const UTM = {
  'chatgpt.com': 'chatgpt',
  chatgpt: 'chatgpt',
  openai: 'chatgpt',
  'copilot.com': 'copilot-bing',
  copilot: 'copilot-bing',
  bing: 'copilot-bing',
  gemini: 'gemini',
  google: 'google',
  perplexity: 'perplexity',
  'perplexity.ai': 'perplexity',
  claude: 'claude',
  'claude.ai': 'claude',
  duckduckgo: 'ddg',
  ddg: 'ddg',
  alert: 'alert',
  tixinfo: 'internal',
};

/** @param {string} h */
const hostSource = (h) => {
  if (!h) return null;
  if (h === 'chatgpt.com' || h.endsWith('.chatgpt.com') || h === 'chat.openai.com') return 'chatgpt';
  if (h === 'copilot.microsoft.com' || h === 'bing.com' || h.endsWith('.bing.com')) return 'copilot-bing';
  if (h === 'gemini.google.com') return 'gemini';
  // google.com, google.co.uk, news.google.com …, and the Google app (android-app://com.google.android.googlequicksearchbox)
  if (/(^|\.)google\.[a-z.]{2,7}$/.test(h) || h === 'com.google.android.googlequicksearchbox') return 'google';
  if (h === 'perplexity.ai' || h.endsWith('.perplexity.ai')) return 'perplexity';
  if (h === 'claude.ai' || h.endsWith('.claude.ai')) return 'claude';
  if (h === 'duckduckgo.com' || h.endsWith('.duckduckgo.com')) return 'ddg';
  if (h === 'tixinfo.com' || h.endsWith('.tixinfo.com') || h.endsWith('.tixinfo.pages.dev')) return 'internal';
  return null;
};

/**
 * The bucket for a landing: utm_source wins (an unknown one is 'other'), else the Referer's host
 * (no referrer: 'none'; a site we do not name: 'other').
 * @param {{ utm?: string | null, referer?: string | null }} o
 * @returns {string}
 */
export const landingSource = ({ utm, referer }) => {
  const u = (utm ?? '').trim().toLowerCase();
  // own keys only: utm_source=constructor (or __proto__) would otherwise return Object's own
  // members, which D1's bind() rejects, and the landing counter would 500 the page
  if (u) return Object.hasOwn(UTM, u) ? UTM[u] : u.endsWith('.chatgpt.com') ? 'chatgpt' : 'other';
  if (!referer) return 'none';
  let host = '';
  try {
    host = new URL(referer).hostname.toLowerCase();
  } catch {
    return 'other';
  }
  return hostSource(host) ?? (host ? 'other' : 'none');
};

/**
 * A bucket as received from a browser (?s=): one of SOURCES, else null.
 * @param {string | null | undefined} raw
 * @returns {string | null}
 */
export const cleanSource = (raw) => (typeof raw === 'string' && SOURCES.includes(raw) ? raw : null);

/**
 * US, CA or other: the country group the counters keep (never the country itself).
 * @param {string | null | undefined} country
 */
export const countryGroup = (country) => (country === 'US' || country === 'CA' ? country : 'other');

/**
 * Autonomous systems of hosting and cloud providers (Cloudflare's request.cf.asn). People browse from home, mobile and office
 * networks; a request that arrives from one of these is a server, a headless browser or a proxy pool. On 2026-10-29 the
 * landing and event-view tallies read US 340 against "other" 12,715, most of it a Singapore headless pool on /api/*, and every
 * click-through rate built on them was deflated. This is a short, documented list of the big providers, not a blocklist: it
 * only decides which line of the tally a view is added to, and nothing is refused.
 * AWS 16509 14618 · Google (Cloud 396982, Google LLC 15169) · Azure 8075 · DigitalOcean 14061 · Hetzner 24940 · OVH 16276 ·
 * Linode/Akamai Connected Cloud 63949 · Vultr 20473 · Oracle Cloud 31898 · Alibaba Cloud 45102 · Tencent Cloud 132203 45090 ·
 * Huawei Cloud 136907 · Contabo 51167 · Scaleway 12876 · M247 9009 (VPN and proxy hosting) · Leaseweb 60781 16265.
 */
export const DC_ASNS = new Set([
  16509, 14618, 396982, 15169, 8075, 14061, 24940, 16276, 63949, 20473, 31898, 45102, 132203, 45090, 136907, 51167, 12876, 9009, 60781, 16265,
]);

/**
 * The line of the cookieless tallies (pv_stat.cc) a request is counted on: 'dc' when it comes from a hosting or cloud network
 * (DC_ASNS), else its country group (US, CA or other). A missing cf is 'other'. Old days hold no 'dc' rows: their datacenter
 * traffic sits in 'other', and the readers (scripts/lib/wave-report.mjs) treat both the same way.
 * @param {{ asn?: number | string | null, country?: string | null } | null | undefined} cf request.cf
 * @returns {'US' | 'CA' | 'other' | 'dc'}
 */
export const trafficGroup = (cf) => {
  const asn = Number(cf?.asn);
  return Number.isInteger(asn) && DC_ASNS.has(asn) ? 'dc' : countryGroup(cf?.country);
};
